import { useEffect, useState, useRef } from "react";
import { useAuth, API } from "../../App";
import UserLayout from "../../components/UserLayout";
import ReferAndEarn from "../../components/ReferAndEarn";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  User, MapPin, LogOut, Sun, Moon, ChevronRight, Plus, Trash2,
  Phone, Mail, Calendar, Edit2, Check, X, Search, Loader2, Home, Briefcase, Star, Navigation, FileText,
  Ticket, BadgeCheck, KeyRound, Eye, EyeOff, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../../hooks/useTheme";
import { GoogleMap, useJsApiLoader, Marker as GMarker, Autocomplete } from "@react-google-maps/api";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const GOOGLE_LIBRARIES = ["places"];

export default function UserProfile() {
  const { user, refreshUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState("profile");

  return (
    <UserLayout showHeader={false}>
      <div className="animate-fade-in" data-testid="user-profile">
        {/* Profile Header */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 text-white p-5 pb-7">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold backdrop-blur-sm">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h1 className="text-lg font-bold">{user?.name || "User"}</h1>
              <p className="text-green-100 text-sm">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sticky top-0 z-10">
          {[
            { id: "profile", label: "Profile", icon: <User size={14} /> },
            { id: "addresses", label: "Addresses", icon: <MapPin size={14} /> },
            { id: "settings", label: "Settings", icon: <Sun size={14} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4 space-y-4">
          {activeTab === "profile" && (
            <>
              <ReferAndEarn />
              {/* Auth/Access codes — pinned at the top with dark styling so users can find their credentials quickly */}
              <div className="rounded-2xl bg-gray-900 text-white border border-gray-800 shadow-sm p-5 space-y-6" data-testid="profile-access-codes-block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <KeyRound size={15} className="text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold tracking-wide uppercase">Your Codes</h2>
                    <p className="text-[10px] text-gray-400">Access codes &amp; authorisation keys to unlock your perks</p>
                  </div>
                </div>
                <SegmentCodeSection dark />
                <AuthCodeSection dark />
              </div>
              <ProfileTab />
            </>
          )}
          {activeTab === "addresses" && <AddressesTab />}
          {activeTab === "settings" && <SettingsTab theme={theme} toggleTheme={toggleTheme} logout={logout} />}
        </div>
      </div>
    </UserLayout>
  );
}

/* ==================== Profile Tab ==================== */
function ProfileTab() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    date_of_birth: "",
    gender: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        date_of_birth: user.date_of_birth || "",
        gender: user.gender || "",
      });
    }
  }, [user]);

  const handleSave = async () => {
    // Phone validation: must be 7-15 digits (with optional + and country code)
    if (form.phone) {
      const cleaned = form.phone.replace(/[\s\-\(\)]/g, "");
      if (!/^\+?\d{7,15}$/.test(cleaned)) {
        toast.error("Please enter a valid phone number (7-15 digits, optionally starting with +)");
        return;
      }
    }
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error("Failed to update profile");
      await refreshUser();
      setEditing(false);
      toast.success("Profile updated");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const genderOptions = ["Male", "Female", "Non-binary", "Prefer not to say"];

  return (
    <div className="space-y-5" data-testid="profile-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Details</h2>
        {!editing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
            <Edit2 size={14} className="mr-1" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
              <X size={14} />
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-profile-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              <span className="ml-1">Save</span>
            </Button>
          </div>
        )}
      </div>

      {/* Email - always read only */}
      <FieldRow icon={<Mail size={18} />} label="Email" value={user?.email || ""} readOnly />

      {/* Name */}
      <FieldRow
        icon={<User size={18} />}
        label="Full Name"
        value={form.name}
        editing={editing}
        onChange={(v) => setForm({ ...form, name: v })}
        testId="profile-name"
      />

      {/* Phone */}
      <FieldRow
        icon={<Phone size={18} />}
        label="Phone"
        value={form.phone}
        editing={editing}
        placeholder="+91 9876543210"
        onChange={(v) => setForm({ ...form, phone: v })}
        testId="profile-phone"
      />

      {/* DOB */}
      <FieldRow
        icon={<Calendar size={18} />}
        label="Date of Birth"
        value={form.date_of_birth}
        editing={editing}
        type="date"
        onChange={(v) => setForm({ ...form, date_of_birth: v })}
        testId="profile-dob"
      />

      {/* Gender */}
      {editing ? (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 mt-1">
            <User size={18} className="text-gray-500" />
          </div>
          <div className="flex-1">
            <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">Gender</Label>
            <div className="flex flex-wrap gap-2">
              {genderOptions.map((g) => (
                <button
                  key={g}
                  data-testid={`gender-${g.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setForm({ ...form, gender: g })}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    form.gender === g
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <FieldRow icon={<User size={18} />} label="Gender" value={form.gender || "Not set"} readOnly />
      )}

      {/* Segment Code redemption */}
      {/* moved above to dedicated dark Codes panel */}

      {/* Authorisation Code (used by Segment Admins to enrol the user) */}
      {/* moved above to dedicated dark Codes panel */}
    </div>
  );
}

/* ==================== Authorization Code (masked + reveal + regenerate) ==================== */
function AuthCodeSection({ dark = false }) {
  const [code, setCode] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchCode = async () => {
    try {
      const r = await fetch(`${API}/user/auth-code`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setCode(data.auth_code || "");
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchCode(); }, []);

  const regenerate = async () => {
    if (!confirm("Regenerate your authorisation code? Any code you've already shared will stop working.")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/user/auth-code/regenerate`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setCode(data.auth_code);
      setRevealed(true);
      toast.success("New authorisation code generated");
    } catch {
      toast.error("Failed to regenerate code");
    } finally { setBusy(false); }
  };

  const masked = code ? "•".repeat(code.length) : "------";

  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => toast.success("Copied"));
  };

  return (
    <div className={dark ? "pt-1" : "pt-6 mt-2 border-t border-gray-100 dark:border-gray-800"} data-testid="auth-code-section">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${dark ? "bg-emerald-500/15" : "bg-emerald-50 dark:bg-emerald-900/40"}`}>
          <KeyRound size={16} className={dark ? "text-emerald-300" : "text-emerald-600 dark:text-emerald-300"} />
        </div>
        <div>
          <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900 dark:text-white"}`}>Authorization Code</h3>
          <p className={`text-[11px] ${dark ? "text-gray-400" : "text-gray-500 dark:text-gray-400"}`}>Share this with your Segment Admin to authorise enrolment. Keep it private.</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={copy}
          disabled={!revealed || !code}
          className={`flex-1 font-mono tracking-[0.4em] text-lg font-bold text-center py-2.5 rounded-xl border disabled:cursor-default ${
            dark
              ? "border-gray-700 bg-black/40 text-white"
              : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
          }`}
          data-testid="auth-code-display"
          title={revealed ? "Tap to copy" : "Reveal first"}
        >
          {loading ? "…" : revealed ? code : masked}
        </button>
        <Button
          type="button"
          variant={dark ? "secondary" : "outline"}
          size="sm"
          onClick={() => setRevealed((v) => !v)}
          disabled={!code}
          className={`px-3 ${dark ? "bg-white/10 text-white hover:bg-white/20 border-0" : ""}`}
          data-testid="auth-code-reveal-btn"
        >
          {revealed ? <><EyeOff size={14} className="mr-1.5" /> Hide</> : <><Eye size={14} className="mr-1.5" /> Reveal</>}
        </Button>
        <Button
          type="button"
          variant={dark ? "secondary" : "outline"}
          size="sm"
          onClick={regenerate}
          disabled={busy}
          className={`px-3 ${dark ? "bg-white/10 text-white hover:bg-white/20 border-0" : ""}`}
          aria-label="Regenerate authorization code"
          title="Regenerate authorization code"
          data-testid="auth-code-regenerate-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>
    </div>
  );
}

/* ==================== Segment Code Redemption ==================== */
function SegmentCodeSection({ dark = false }) {
  const { refreshUser } = useAuth();
  const [mySegments, setMySegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchMine = async () => {
    try {
      const r = await fetch(`${API}/user/segments`, { credentials: "include" });
      if (r.ok) setMySegments(await r.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const redeem = async (e) => {
    e.preventDefault();
    const raw = code.trim().toUpperCase();
    if (raw.length < 4) { toast.error("Enter a valid code"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/user/segment-code/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: raw }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      toast.success(`You're now part of ${data.segment.name}!`);
      setCode("");
      await fetchMine();
      try { await refreshUser?.(); } catch { /* noop */ }
    } catch (e2) {
      toast.error(e2.message || "Failed to redeem code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={dark ? "pt-1 pb-1 border-b border-white/10" : "pt-6 mt-2 border-t border-gray-100 dark:border-gray-800"} data-testid="segment-code-section">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${dark ? "bg-emerald-500/15" : "bg-green-50 dark:bg-emerald-900/40"}`}>
          <Ticket size={16} className={dark ? "text-emerald-300" : "text-green-600 dark:text-emerald-300"} />
        </div>
        <div>
          <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900 dark:text-white"}`}>Access Code</h3>
          <p className={`text-[11px] ${dark ? "text-gray-400" : "text-gray-500 dark:text-gray-400"}`}>Have a 6-character code from your organisation? Unlock your perks here.</p>
        </div>
      </div>

      <form onSubmit={redeem} className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABC123"
          maxLength={12}
          className={`font-mono tracking-widest uppercase flex-1 ${dark ? "bg-black/40 border-gray-700 text-white placeholder:text-gray-500" : ""}`}
          data-testid="segment-code-input"
        />
        <Button type="submit" disabled={submitting || !code.trim()} className="bg-green-600 hover:bg-green-700" data-testid="segment-code-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : "Redeem"}
        </Button>
      </form>

      {loading ? null : mySegments.length > 0 && (
        <div className="mt-4" data-testid="segment-code-active-memberships">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${dark ? "text-gray-400" : "text-gray-400"}`}>Your memberships</p>
          <div className="flex flex-wrap gap-1.5">
            {mySegments.map((s) => (
              <span
                key={s.segment_id}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
                  dark
                    ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                    : "bg-green-50 text-green-700 border border-green-100 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/50"
                }`}
              >
                <BadgeCheck size={12} /> {s.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ icon, label, value, editing, onChange, type = "text", placeholder, readOnly, testId }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 mt-1">
        <span className="text-gray-500">{icon}</span>
      </div>
      <div className="flex-1">
        <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">{label}</Label>
        {editing && !readOnly ? (
          <Input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || label}
            className="h-9"
            data-testid={testId}
          />
        ) : (
          <p className="text-sm font-medium text-gray-900 dark:text-white" data-testid={testId}>
            {value || "Not set"}
          </p>
        )}
      </div>
    </div>
  );
}

/* ==================== Addresses Tab ==================== */
function AddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddr, setEditingAddr] = useState(null);
  const [billingAddr, setBillingAddr] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [showBillingForm, setShowBillingForm] = useState(false);

  useEffect(() => {
    fetchAddresses();
    fetchBillingAddress();
  }, []);

  const fetchAddresses = async () => {
    try {
      const res = await fetch(`${API}/addresses`, { credentials: "include" });
      if (res.ok) setAddresses(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingAddress = async () => {
    try {
      const res = await fetch(`${API}/billing-address`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBillingAddr(data.user_id ? data : null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleDelete = async (addrId) => {
    try {
      const res = await fetch(`${API}/addresses/${addrId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setAddresses((prev) => prev.filter((a) => a.address_id !== addrId));
        toast.success("Address deleted");
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteBilling = async () => {
    try {
      const res = await fetch(`${API}/billing-address`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setBillingAddr(null);
        toast.success("Billing address deleted");
      }
    } catch {
      toast.error("Failed to delete billing address");
    }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingAddr(null);
    fetchAddresses();
  };

  const handleBillingSaved = () => {
    setShowBillingForm(false);
    fetchBillingAddress();
  };

  const labelIcons = { Home: <Home size={14} />, Work: <Briefcase size={14} />, Other: <Star size={14} /> };

  if (showForm || editingAddr) {
    return (
      <AddressForm
        address={editingAddr}
        onSave={handleSaved}
        onCancel={() => { setShowForm(false); setEditingAddr(null); }}
      />
    );
  }

  if (showBillingForm) {
    return (
      <BillingAddressForm
        address={billingAddr}
        onSave={handleBillingSaved}
        onCancel={() => setShowBillingForm(false)}
      />
    );
  }

  return (
    <div data-testid="addresses-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Delivery Addresses</h2>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-address-btn">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />)}</div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
          <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No saved addresses</p>
          <Button variant="link" onClick={() => setShowForm(true)} className="text-green-600 mt-2">Add your first address</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((addr) => (
            <div key={addr.address_id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4" data-testid={`address-${addr.address_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {labelIcons[addr.label] || <MapPin size={14} className="text-green-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900 dark:text-white">{addr.label}</span>
                      {addr.is_default && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Default</span>}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{addr.address_line}</p>
                    {addr.landmark && <p className="text-[10px] text-gray-500">{addr.landmark}</p>}
                    <p className="text-[10px] text-gray-500 mt-0.5">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditingAddr(addr)} className="p-2 text-gray-400 hover:text-green-600 transition-colors" data-testid={`edit-address-${addr.address_id}`}>
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(addr.address_id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-address-${addr.address_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Billing Address Section */}
      <div className="mt-8" data-testid="billing-address-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Billing Address</h2>
            <p className="text-[10px] text-gray-500 mt-0.5">Used on your order invoices</p>
          </div>
          {!billingAddr && (
            <Button size="sm" onClick={() => setShowBillingForm(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-billing-address-btn">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          )}
        </div>
        {billingLoading ? (
          <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
        ) : !billingAddr ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
            <Briefcase className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">No billing address set</p>
            <Button variant="link" onClick={() => setShowBillingForm(true)} className="text-green-600 mt-1 text-xs">Add billing address</Button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-xl p-4" data-testid="billing-address-card">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Briefcase size={14} className="text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">Billing</span>
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Invoice</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{billingAddr.address_line}</p>
                  {billingAddr.landmark && <p className="text-[10px] text-gray-500">{billingAddr.landmark}</p>}
                  <p className="text-[10px] text-gray-500 mt-0.5">{[billingAddr.city, billingAddr.state, billingAddr.pincode].filter(Boolean).join(", ")}</p>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => setShowBillingForm(true)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" data-testid="edit-billing-address-btn">
                  <Edit2 size={14} />
                </button>
                <button onClick={handleDeleteBilling} className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-testid="delete-billing-address-btn">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== Address Form with Map ==================== */
function AddressForm({ address, onSave, onCancel }) {
  const [form, setForm] = useState({
    label: address?.label || "Home",
    address_line: address?.address_line || "",
    landmark: address?.landmark || "",
    city: address?.city || "",
    state: address?.state || "",
    pincode: address?.pincode || "",
    lat: address?.lat || 20.5937,
    lng: address?.lng || 78.9629,
    is_default: address?.is_default || false,
  });
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY, libraries: GOOGLE_LIBRARIES });

  const indiaBounds = { north: 35.5, south: 6.5, east: 97.5, west: 68.0 };

  useEffect(() => {
    if (!address && navigator.geolocation) {
      setGeoLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setForm((prev) => ({ ...prev, lat: latitude, lng: longitude }));
          reverseGeocode(latitude, longitude);
          setGeoLoading(false);
        },
        () => setGeoLoading(false),
        { timeout: 8000, enableHighAccuracy: true }
      );
    }
  }, []);

  const reverseGeocode = async (lat, lng) => {
    if (!window.google?.maps) return;
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      const result = response.results?.[0];
      if (!result) return;
      const components = result.address_components || [];
      let city = "", state = "", pincode = "";
      for (const c of components) {
        if (c.types.includes("locality")) city = c.long_name;
        if (c.types.includes("administrative_area_level_1")) state = c.long_name;
        if (c.types.includes("postal_code")) pincode = c.long_name;
        if (!city && c.types.includes("sublocality_level_1")) city = c.long_name;
      }
      setForm((prev) => ({
        ...prev, lat, lng,
        address_line: result.formatted_address?.split(",").slice(0, 3).join(",").trim() || prev.address_line,
        city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode,
      }));
    } catch (e) {
      console.error("Reverse geocode failed:", e);
    }
  };

  const onPlaceSelected = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const components = place.address_components || [];
    let city = "", state = "", pincode = "";
    for (const c of components) {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_1")) state = c.long_name;
      if (c.types.includes("postal_code")) pincode = c.long_name;
      if (!city && c.types.includes("sublocality_level_1")) city = c.long_name;
    }
    setForm((prev) => ({
      ...prev, lat, lng,
      address_line: place.formatted_address?.split(",").slice(0, 3).join(",").trim() || place.name || prev.address_line,
      city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode,
    }));
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setForm((prev) => ({ ...prev, lat, lng }));
    reverseGeocode(lat, lng);
  };

  const handleSubmit = async () => {
    if (!form.address_line.trim()) { toast.error("Please enter an address"); return; }
    if (!form.city.trim() || !form.state.trim()) { toast.error("City and State are required"); return; }
    setSaving(true);
    try {
      const url = address ? `${API}/addresses/${address.address_id}` : `${API}/addresses`;
      const method = address ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to save address");
      toast.success(address ? "Address updated" : "Address saved");
      onSave();
    } catch (e) {
      toast.error("Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  const labelOptions = ["Home", "Work", "Other"];

  return (
    <div data-testid="address-form" className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{address ? "Edit Address" : "Add Address"}</h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="cancel-address-form"><X size={20} /></button>
      </div>

      {/* Google Places Search */}
      {isLoaded && (
        <Autocomplete
          onLoad={(auto) => (autocompleteRef.current = auto)}
          onPlaceChanged={onPlaceSelected}
          options={{ componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] }}
        >
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search for a location in India..." className="pl-9 h-10" data-testid="location-search-input" />
          </div>
        </Autocomplete>
      )}

      {/* Google Map */}
      {isLoaded ? (
        <div className="h-48 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: form.lat, lng: form.lng }}
            zoom={13}
            onClick={handleMapClick}
            onLoad={(map) => { mapRef.current = map; }}
            options={{ restriction: { latLngBounds: indiaBounds, strictBounds: true }, mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
          >
            <GMarker position={{ lat: form.lat, lng: form.lng }} />
          </GoogleMap>
        </div>
      ) : (
        <div className="h-48 rounded-xl bg-gray-100 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
      )}
      <p className="text-[10px] text-gray-500 dark:text-gray-200 -mt-2">Tap on the map to select location</p>

      {/* Use Current Location */}
      <button type="button" onClick={() => {
        if (!navigator.geolocation) return;
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            setForm((prev) => ({ ...prev, lat: latitude, lng: longitude }));
            reverseGeocode(latitude, longitude);
            mapRef.current?.panTo({ lat: latitude, lng: longitude });
            mapRef.current?.setZoom(16);
            setGeoLoading(false);
            toast.success("Location captured!");
          },
          () => { setGeoLoading(false); toast.error("Could not get location"); },
          { timeout: 8000, enableHighAccuracy: true }
        );
      }} className="flex items-center gap-2 text-sm text-blue-600 font-medium hover:text-blue-700 -mt-1" data-testid="use-current-location-btn">
        <Navigation size={14} /> {geoLoading ? "Detecting..." : "Use Current Location"}
      </button>

      {/* Label */}
      <div>
        <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-2 block">Address Label</Label>
        <div className="flex gap-2">
          {labelOptions.map((l) => (
            <button key={l} onClick={() => setForm({ ...form, label: l })} data-testid={`label-${l.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${form.label === l ? "bg-green-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Form Fields */}
      <div>
        <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">Address Line *</Label>
        <Input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="House/Flat No., Street, Area" data-testid="address-line-input" />
      </div>
      <div>
        <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">Landmark</Label>
        <Input value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} placeholder="Near..." data-testid="address-landmark-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="address-city-input" />
        </div>
        <div>
          <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">State</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="address-state-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] text-gray-400 dark:text-gray-400 mb-1 block">Pincode</Label>
          <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="address-pincode-input" />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="address-default-toggle">
            <Switch checked={form.is_default} onCheckedChange={(val) => setForm({ ...form, is_default: val })} />
            <span className="text-sm text-gray-600 dark:text-gray-300">Default</span>
          </label>
        </div>
      </div>

      {/* Submit */}
      <div className="pb-24">
        <Button onClick={handleSubmit} disabled={saving} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3" data-testid="save-address-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (address ? "Update Address" : "Save Address")}
        </Button>
      </div>
    </div>
  );
}

/* ==================== Billing Address Form ==================== */
function BillingAddressForm({ address, onSave, onCancel }) {
  const [form, setForm] = useState({
    address_line: address?.address_line || "",
    landmark: address?.landmark || "",
    city: address?.city || "",
    state: address?.state || "",
    pincode: address?.pincode || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.address_line.trim()) { toast.error("Address line is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/billing-address`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Billing address saved");
      onSave();
    } catch {
      toast.error("Failed to save billing address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="billing-address-form">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onCancel} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg" data-testid="billing-form-back">
          <X size={16} className="text-gray-500" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{address ? "Edit" : "Add"} Billing Address</h2>
      </div>
      <p className="text-[10px] text-gray-500 mb-4">This address will appear on your order invoices.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] text-gray-400">Address Line *</Label>
          <Input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="123, Main Street, Suite 4B" className="mt-1" data-testid="billing-address-line" />
        </div>
        <div>
          <Label className="text-[10px] text-gray-400">Landmark</Label>
          <Input value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} placeholder="Near XYZ mall" className="mt-1" data-testid="billing-landmark" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] text-gray-400">City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Mumbai" className="mt-1" data-testid="billing-city" />
          </div>
          <div>
            <Label className="text-[10px] text-gray-400">State</Label>
            <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Maharashtra" className="mt-1" data-testid="billing-state" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-gray-400">Pincode</Label>
          <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="400001" className="mt-1" data-testid="billing-pincode" />
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="save-billing-address-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {saving ? "Saving..." : "Save Billing Address"}
        </Button>
      </div>
    </div>
  );
}

/* ==================== Settings Tab ==================== */
function SettingsTab({ theme, toggleTheme, logout }) {
  const handleLogout = async () => {
    // IMPORTANT: await the backend POST /auth/logout so the cookie is cleared
    // BEFORE we reload. Without the await, window.location.href fires first,
    // the page reloads while the cookie is still valid, /auth/me succeeds,
    // and the user is silently re-authenticated. Looks like "logout failed".
    try { await logout(); } catch { /* even on error, force navigation */ }
    window.location.href = "/";
  };

  return (
    <div className="space-y-4" data-testid="settings-tab">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>

      {/* Customer Care */}
      <a
        href="tel:+919621886633"
        className="flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors"
        data-testid="customer-care-link"
      >
        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
          <Phone size={18} className="text-green-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Customer Care</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-400">Tap to call +91 96218 86633</p>
        </div>
        <ChevronRight size={16} className="text-gray-400" />
      </a>

      {/* Theme Toggle */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {theme === "dark" ? <Moon size={20} className="text-indigo-400" /> : <Sun size={20} className="text-amber-500" />}
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</p>
            <p className="text-[10px] text-gray-400 dark:text-gray-400">Switch between light and dark theme</p>
          </div>
        </div>
        <Switch checked={theme === "dark"} onCheckedChange={toggleTheme} data-testid="theme-toggle" />
      </div>

      {/* Policies */}
      <div className="space-y-2 mb-4">
        <a href="/privacy-policy" className="w-full flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition-colors" data-testid="privacy-policy-link">
          <FileText size={20} />
          <span className="font-medium text-sm">Privacy Policy</span>
          <ChevronRight size={16} className="ml-auto text-gray-400" />
        </a>
        <a href="/refund-policy" className="w-full flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition-colors" data-testid="refund-policy-link">
          <FileText size={20} />
          <span className="font-medium text-sm">Refund Policy</span>
          <ChevronRight size={16} className="ml-auto text-gray-400" />
        </a>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl p-4 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        data-testid="logout-btn"
      >
        <LogOut size={20} />
        <span className="font-medium text-sm">Logout</span>
        <ChevronRight size={16} className="ml-auto" />
      </button>
    </div>
  );
}
